/* The translations: `src/i18n/<locale>/<namespace>.ts`, one folder per locale mirroring
 * `src/i18n/en/` file for file (w7-03 writes them). Each file default-exports a flat object of
 * the same keys as its English file:
 *
 *   // src/i18n/de/S1.ts
 *   export default { title: 'Wohin sind Sie geflogen?', … };
 *
 * - A missing file, or a missing key in a file, shows the English text (i18n.tsx).
 * - Plurals: where English has `key_one` / `key_other`, a locale adds whatever categories
 *   `Intl.PluralRules(<locale>)` gives it (`key_zero`, `key_two`, `key_few`, `key_many`).
 * - `{placeholders}` as in English. Keys English doesn't have are ignored (loader.test.ts lists
 *   them when files are present).
 *
 * English is in the entry and the stage chunks (messages.ts). Every other locale is one lazy
 * chunk (`locale-<code>`, vite.config.ts's db-locales plugin finds the folders at build time),
 * fetched when the visit starts in it or the visitor picks it in the sheet: never part of the
 * initial route.
 */
import type { Locale } from './registry';

/** namespace → key → text. */
export type Dict = Record<string, Record<string, string>>;

export type LocaleSource = (l: Locale) => Promise<Dict | null>;

/** The catalogue is a chunk of its own too: an English visit downloads none of this. */
async function fromFiles(l: Locale): Promise<Dict | null> {
  const { default: catalogue } = await import('virtual:db-locales');
  const load = catalogue[l];
  return load ? (await load()).default : null;
}

let source: LocaleSource = fromFiles;

/** Tests, the dev drawer and the screenshot script: dictionaries from elsewhere (null: the files). */
export function setLocaleSource(s: LocaleSource | null) {
  source = s ?? fromFiles;
}

/** A locale's dictionary, or null when this build has no copy for it (English everywhere). */
export function loadLocale(l: Locale): Promise<Dict | null> {
  return l === 'en' ? Promise.resolve(null) : source(l);
}

/** The locales with copy in this build (tests, the dev drawer). */
export async function bundledLocales(): Promise<string[]> {
  return Object.keys((await import('virtual:db-locales')).default);
}
